import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { Modal } from '@/components/molecules/Modal';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { useProductsReport, useExportReportMutation } from '@/hooks/useErpApi';
import type { ProductReportFilter, ProductReportRecord } from '@kisanlink/erp-service';

export default function ProductsReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ProductReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedRecord, setSelectedRecord] = useState<ProductReportRecord | null>(null);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useProductsReport({ ...filters, limit: itemsPerPage, offset });
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as ProductReportFilter);
    setCurrentPage(1);
  }, []);
  
  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({
          reportType: 'products',
          filters: { ...filters, format },
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `products-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [filters, exportReport]
  );

  const columns: TableColumn<ProductReportRecord>[] = [
    {
      key: 'name',
      header: 'Product Name',
      render: (row) => (
        <div>
          <div className="font-medium text-neutral-900">{row.name}</div>
          {row.description && (
            <div className="text-xs text-neutral-500 truncate max-w-xs">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'variant_count',
      header: 'Variants',
      render: (row) => (
        <Badge variant={row.variant_count > 0 ? 'info' : 'neutral'}>{row.variant_count}</Badge>
      ),
    },
    {
      key: 'total_stock',
      header: 'Stock',
      render: (row) => (
        <span className={row.total_stock > 0 ? 'text-success-700 font-medium' : 'text-error-600'}>
          {row.total_stock.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: 'updated_at',
      header: 'Updated',
      render: (row) => new Date(row.updated_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedRecord(row)}
          className="text-neutral-600 hover:text-primary-600"
        >
          <Icon name="Eye" size={16} />
        </Button>
      ),
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Products',
          value: summary.total_products,
          icon: 'Package' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Variants',
          value: summary.total_variants,
          icon: 'Layers' as const,
          color: 'info' as const,
          format: 'number' as const,
        },
        {
          label: 'Products with Stock',
          value: summary.products_with_stock,
          icon: 'CircleCheck' as const,
          color: 'success' as const,
          format: 'number' as const,
        },
        {
          label: 'Out of Stock',
          value: summary.total_products - summary.products_with_stock,
          icon: 'CircleAlert' as const,
          color: 'warning' as const,
          format: 'number' as const,
        },
      ]
    : [];

  // Pagination is handled by the Pagination component below

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Products Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Product catalog with variant counts and stock levels
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={false}
        showSearch={true}
        searchPlaceholder="Search products..."
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No products found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        title="Product Details"
        size="lg"
      >
        {selectedRecord && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500 mb-1">Product ID</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Product Name</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.name}</p>
              </div>
              {selectedRecord.external_id && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">External ID</p>
                  <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.external_id}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-neutral-500 mb-1">Description</p>
                <p className="text-sm text-neutral-900">{selectedRecord.description || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Variant Count</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.variant_count}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Total Stock</p>
                <p className={`text-sm font-medium ${selectedRecord.total_stock > 0 ? 'text-success-700' : 'text-error-600'}`}>
                  {selectedRecord.total_stock.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Created At</p>
                <p className="text-sm text-neutral-900">{new Date(selectedRecord.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Updated At</p>
                <p className="text-sm text-neutral-900">{new Date(selectedRecord.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
